import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Wordmark } from "@/components/Wordmark";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";

type Search = { mode?: "signin" | "signup" | "reset"; redirect?: string };

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: buildAppMeta({
      title: "Sign in — CoachQuick",
      description: "Sign in or create a CoachQuick account to manage your clients, sessions, and billing.",
      path: "/auth",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/auth` }],
  }),
  validateSearch: (s: Record<string, unknown>): Search => ({
    mode: (s.mode as Search["mode"]) ?? "signin",
    redirect: (s.redirect as string) || undefined,
  }),
  component: AuthPage,
});

function AuthPage() {
  const { mode, redirect: redirectParam } = Route.useSearch();
  const isSignup = mode === "signup";
  const isReset = mode === "reset";
  const nav = useNavigate();
  const { session, role, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  async function handleResend() {
    if (!signedUpEmail || resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: signedUpEmail,
        options: { emailRedirectTo: `${window.location.origin}/auth` },
      });
      if (error) {
        const match = /(\d+)\s*seconds?/i.exec(error.message);
        if (match) setResendCooldown(parseInt(match[1], 10));
        toast.error(error.message);
      } else {
        toast.success("Confirmation email re-sent");
        setResendCooldown(40);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResending(false);
    }
  }

  useEffect(() => {
    if (!loading && session) {
      if (redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")) {
        window.location.href = redirectParam;
        return;
      }
      // Send brand-new coaches to the onboarding wizard.
      (async () => {
        const { data } = await supabase
          .from("coach_onboarding" as never)
          .select("coach_id, completed_at, skipped")
          .eq("coach_id", session.user.id)
          .maybeSingle();
        const row = data as { completed_at: string | null; skipped: boolean } | null;
        if (!row || (!row.completed_at && !row.skipped)) {
          nav({ to: "/app/onboarding" });
        } else {
          nav({ to: "/app" });
        }
      })();
    }
  }, [loading, session, role, nav, redirectParam]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (isReset) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        // Never reveal whether the address has an account.
        if (error && !/rate|seconds/i.test(error.message)) {
          console.warn("reset request failed");
        } else if (error) {
          toast.error(error.message);
          return;
        }
        setResetSent(true);
        setResendCooldown(40);
      } else if (isSignup) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth`,
            data: { full_name: name, role: "coach" },
          },
        });
        if (error) throw error;
        setSignedUpEmail(email);
        setResendCooldown(40);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome back");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center" aria-label="CoachQuick home">
          <Wordmark className="text-2xl font-extrabold tracking-tight" />
        </Link>
        <Card className="p-8 shadow-elevated">
          {isReset ? (
            resetSent ? (
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-brand-text">
                  <MailCheck className="h-6 w-6" />
                </div>
                <h1 className="mt-4 text-2xl font-bold tracking-tight">Check your email</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  If an account exists for{" "}
                  <strong className="text-foreground">{email}</strong>, a password reset link is on
                  its way. The link expires shortly, so grab it while it's warm.
                </p>
                <div className="mt-6 space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={resendCooldown > 0 || busy}
                    onClick={(e) => submit(e as unknown as React.FormEvent)}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend reset link"}
                  </Button>
                  <Button asChild variant="ghost" className="w-full">
                    <Link to="/auth" search={{ mode: "signin" } as never}>
                      Back to sign in
                    </Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Reset your password</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter your email and we'll send you a link to set a new one.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={busy}>
                    {busy ? "Sending…" : "Send reset link"}
                  </Button>
                </form>
                <p className="mt-6 text-center text-sm text-muted-foreground">
                  Remembered it?{" "}
                  <Link
                    to="/auth"
                    search={{ mode: "signin" } as never}
                    className="font-medium text-brand-text hover:underline"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            )
          ) : signedUpEmail ? (
            <div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-brand-text">
                <MailCheck className="h-6 w-6" />
              </div>
              <h1 className="mt-4 text-2xl font-bold tracking-tight">Check your email</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We sent a confirmation link to <strong className="text-foreground">{signedUpEmail}</strong>.
                Click it to verify your account, then come back here to sign in.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Didn't see it? Check your spam folder.
              </p>
              <div className="mt-6 space-y-2">
                <Button
                  type="button"
                  className="w-full"
                  variant="outline"
                  disabled={resendCooldown > 0 || resending}
                  onClick={handleResend}
                >
                  {resending
                    ? "Resending…"
                    : resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Resend confirmation email"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setSignedUpEmail(null);
                    setResendCooldown(0);
                  }}
                >
                  Use a different email
                </Button>
              </div>
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Already verified?{" "}
                <Link
                  to="/auth"
                  search={{ mode: "signin" } as never}
                  className="font-medium text-brand-text hover:underline"
                  onClick={() => setSignedUpEmail(null)}
                >
                  Sign in
                </Link>
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold tracking-tight">
                {isSignup ? "Create your coach account" : "Welcome back"}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {isSignup ? "Start running your coaching business in minutes." : "Sign in to your dashboard."}
              </p>
              <div className="mt-6 space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const redirectQuery = redirectParam
                        ? `?redirect=${encodeURIComponent(redirectParam)}`
                        : "";
                      const result = await lovable.auth.signInWithOAuth("google", {
                        redirect_uri: `${window.location.origin}/auth${redirectQuery}`,
                      });
                      if (result.error) {
                        toast.error(result.error instanceof Error ? result.error.message : "Google sign-in failed");
                      }
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : "Google sign-in failed");
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                  </svg>
                  Continue with Google
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or continue with email</span>
                  </div>
                </div>
              </div>
              <form onSubmit={submit} className="mt-4 space-y-4">
                {isSignup && (
                  <div>
                    <Label htmlFor="name">Full name</Label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" />
                  </div>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {!isSignup && (
                      <Link
                        to="/auth"
                        search={{ mode: "reset" } as never}
                        className="text-xs font-medium text-brand-text hover:underline"
                      >
                        Forgot password?
                      </Link>
                    )}
                  </div>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" />
                </div>
                <Button type="submit" className="w-full" disabled={busy}>
                  {busy ? (isSignup ? "Creating account…" : "Signing in…") : isSignup ? "Create account" : "Sign in"}
                </Button>
              </form>
              <p className="mt-6 text-center text-sm text-muted-foreground">
                {isSignup ? "Already have an account?" : "New to CoachQuick?"}{" "}
                <Link
                  to="/auth"
                  search={{ mode: isSignup ? "signin" : "signup" } as never}
                  className="font-medium text-brand-text hover:underline"
                >
                  {isSignup ? "Sign in" : "Create one"}
                </Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
